/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo } from 'react';

type UseQidList = {
  qid: QIDData[];
  loadingQid: boolean;
  reloadQid: () => void;
};

const useQidList = (qid: string): UseQidList => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['useQidList', qid], [qid]);

  // Fetch communication data using react-query
  const { data, isLoading, isFetching } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        if (!qid) return { data: [] };
        const request = {
          url: process.env[`REACT_APP_REST_BASE`] + `public/qid/${qid}/list`,
          method: 'GET',
        };
        const response = await axios(request);

        return response;
      } catch (error) {
        console.error(`useQidList.${qid}.caught: ${error.message}`);
        // Simulated fetch call. Replace this with actual API call.
        return {
          data: [],
        };
      }
    },
    refetchOnWindowFocus: false,
  });

  const reloadQid = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  return {
    qid: !isLoading && Array.isArray(data.data) ? data.data : [],
    loadingQid: isLoading || isFetching,
    reloadQid,
  };
};

export default useQidList;
